"use client";

import { useMemo, type ReactNode } from "react";
import { clientApi } from "@frontend/lib/api";
import { collabsCacheKey } from "@frontend/lib/data-cache";
import { useAsync } from "@frontend/lib/use-async";
import {
  WorkspaceSideProvider,
  type WorkspaceSide,
} from "@frontend/lib/workspace-side";
import type { Viewer } from "@creators/backend/lib/auth";
import type { CollabListRow } from "@creators/backend/lib/collabs/queries";

/**
 * Loads the inbox once (shared cache with TopNav) and decides whether the
 * signed-in account can flip between creator and collaborator.
 */
export function WorkspaceSideGate({
  viewer,
  children,
}: {
  viewer: Viewer;
  children: ReactNode;
}) {
  const inbox = useAsync(
    async () => {
      if (viewer.isAdmin) return [] as CollabListRow[];
      const { collabs } = await clientApi<{ collabs: CollabListRow[] }>("/api/collabs");
      return collabs;
    },
    [viewer.sessionId, viewer.isAdmin, viewer.creator?.id],
    { cacheKey: collabsCacheKey(viewer.sessionId, viewer.creator?.id) },
  );

  const collabs = inbox.data ?? [];

  const { canToggle, forcedSide } = useMemo(() => {
    // Shared/team accounts are always dual-sided (deliver + book as that account).
    // Do not wait on viewer.brand — older APIs cleared it when !own and hid the toggle.
    const activeAccount = viewer.creator
      ? viewer.accounts.find((a) => a.creatorId === viewer.creator!.id)
      : null;
    const delegated = Boolean(
      (viewer.membership && !viewer.membership.own) || (activeAccount && !activeAccount.own),
    );
    const hasCreatorCollab = collabs.some((c) => c.yourRole === "creator");
    const hasBrandCollab = collabs.some((c) => c.yourRole === "brand");
    const hasCreatorProfile = Boolean(viewer.creator);
    const hasBrandProfile = Boolean(viewer.brand);

    const creatorPossible = hasCreatorProfile || hasCreatorCollab;
    const brandPossible = hasBrandProfile || hasBrandCollab || delegated;
    const canToggle = creatorPossible && brandPossible;

    let forcedSide: WorkspaceSide | null = null;
    if (!canToggle) {
      forcedSide = brandPossible && !creatorPossible ? "brand" : "creator";
    }

    return { canToggle, forcedSide };
  }, [collabs, viewer.creator, viewer.brand, viewer.membership, viewer.accounts]);

  const serverSide =
    !viewer.isAdmin && (viewer.workspaceSide === "creator" || viewer.workspaceSide === "brand")
      ? viewer.workspaceSide
      : null;

  return (
    <WorkspaceSideProvider
      canToggle={canToggle}
      forcedSide={forcedSide}
      serverSide={serverSide}
    >
      {children}
    </WorkspaceSideProvider>
  );
}
